import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { notificationEndpointsApi } from '../services/api';
import { useAnalytics } from '../hooks/useAnalytics';

interface NotificationEndpoint {
  id: string;
  type: 'email' | 'whatsapp';
  value: string;
  is_active: boolean;
  created_at: string;
  verification_status?: 'pending' | 'verified';
}

interface SettingsProps {
  authState: any;
}

const Settings: React.FC<SettingsProps> = ({ authState }) => {
  const { trackButtonClick, trackFormSubmission, trackEmailEvent, trackResendEvent } = useAnalytics();
  
  const [endpoints, setEndpoints] = useState<NotificationEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEndpoint, setNewEndpoint] = useState({
    type: 'email' as 'email' | 'whatsapp',
    value: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerifyMsg, setShowVerifyMsg] = useState(false);

  useEffect(() => {
    fetchEndpoints();
    // eslint-disable-next-line
  }, []);

  const fetchEndpoints = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await notificationEndpointsApi.getEndpoints();
      setEndpoints(res.data || []);
    } catch (err: any) {
      setError('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEndpoint = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEndpoint.value.trim()) {
      setError('Please enter a valid endpoint value');
      return;
    }
    try {
      setIsSubmitting(true);
      setError(null);

      // Validate email format if type is email
      if (newEndpoint.type === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEndpoint.value)) {
          setError('Please enter a valid email address');
          return;
        }
      }

      // Validate phone number format if type is whatsapp
      if (newEndpoint.type === 'whatsapp') {
        const phoneRegex = /^\+?[1-9]\d{1,14}$/;
        if (!phoneRegex.test(newEndpoint.value.replace(/\s/g, ''))) {
          setError('Please enter a valid phone number (e.g., +919876543210)');
          return;
        }
      }

      // Check for duplicates
      const isDuplicate = endpoints.some(endpoint => 
        endpoint.type === newEndpoint.type && 
        endpoint.value.toLowerCase() === newEndpoint.value.toLowerCase()
      );

      if (isDuplicate) {
        setError('This endpoint already exists');
        return;
      }
      
      trackButtonClick('add_email', '/settings');
      trackEmailEvent('add_email', newEndpoint.type);
      
      await notificationEndpointsApi.addEndpoint(newEndpoint);
      trackFormSubmission('add_email', true);
      setShowVerifyMsg(newEndpoint.type === 'email');
      setNewEndpoint({ type: 'email', value: '' });
      setShowAddForm(false);
      fetchEndpoints();
    } catch (err: any) {
      trackFormSubmission('add_email', false, err.message);
      setError(err.message || 'Failed to add notification endpoint');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async (endpointId: string) => {
    try {
      setError(null);
      trackButtonClick('resend_verification', '/settings');
      trackResendEvent('resend_verification', 'email');
      
      await notificationEndpointsApi.resendVerification(endpointId);
      trackResendEvent('resend_verification', 'email', true);
      setShowVerifyMsg(true);
    } catch (err: any) {
      trackResendEvent('resend_verification', 'email', false);
      setError('Failed to resend verification email');
    }
  };

  const handleDeleteEndpoint = async (endpointId: string) => {
    try {
      setError(null);
      await notificationEndpointsApi.deleteEndpoint(endpointId);
      setEndpoints(prev => prev.filter(endpoint => endpoint.id !== endpointId));
    } catch (err: any) {
      setError('Failed to delete notification endpoint');
    }
  };

  const isPrimaryEmail = (endpoint: NotificationEndpoint) => {
    return endpoint.type === 'email' && endpoint.value === authState.user?.email;
  };

  const tierDisplayName: Record<string, string> = {
    basic: 'Basic',
    advanced: 'Advanced',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600">Manage your account preferences and notification settings</p>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
              <p className="text-sm text-gray-600">
                Manage where you receive alerts and notifications
              </p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add
            </button>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {showVerifyMsg && (
            <div className="mb-4 text-yellow-700 bg-yellow-100 border border-yellow-200 px-4 py-2 rounded">
              Please check your email and click the verification link to activate notifications.
            </div>
          )}

          {/* Add New Endpoint Form */}
          {showAddForm && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-md font-semibold text-gray-900 mb-4">Add New Notification</h3>
              <form onSubmit={handleAddEndpoint} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <select
                      value={newEndpoint.type}
                      onChange={(e) => setNewEndpoint(prev => ({ ...prev, type: e.target.value as 'email' | 'whatsapp' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="email">Email</option>
                      <option value="whatsapp">WhatsApp</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {newEndpoint.type === 'email' ? 'Email Address' : 'Phone Number'}
                    </label>
                    <input
                      type={newEndpoint.type === 'email' ? 'email' : 'tel'}
                      value={newEndpoint.value}
                      onChange={(e) => setNewEndpoint(prev => ({ ...prev, value: e.target.value }))}
                      placeholder={newEndpoint.type === 'email' ? 'your.email@example.com' : '+919876543210'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? 'Adding...' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setNewEndpoint({ type: 'email', value: '' });
                      setError(null);
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Endpoints List */}
          <div className="space-y-4">
            {endpoints.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <p className="text-gray-500">No notification endpoints configured</p>
                <p className="text-sm text-gray-400">Add an endpoint to receive alerts</p>
              </div>
            ) : (
              endpoints.map((endpoint) => (
                <div
                  key={endpoint.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    endpoint.type === 'email' && endpoint.verification_status === 'pending'
                      ? 'bg-yellow-50 border-yellow-200'
                      : isPrimaryEmail(endpoint)
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${
                      endpoint.type === 'email' ? 'bg-blue-100' : 'bg-green-100'
                    }`}>
                      <svg className={`w-5 h-5 ${
                        endpoint.type === 'email' ? 'text-blue-600' : 'text-green-600'
                      }`} fill="currentColor" viewBox="0 0 20 20">
                        {endpoint.type === 'email' ? (
                          <>
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                          </>
                        ) : (
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.346.033L7.246 4.668zM12 10a2 2 0 11-4 0 2 2 0 014 0z" clipRule="evenodd" />
                        )}
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className={`font-medium truncate ${
                          isPrimaryEmail(endpoint) ? 'text-gray-500' : 'text-gray-900'
                        }`}>
                          {endpoint.value}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <p className="text-sm text-gray-500 capitalize">
                          {endpoint.type}
                        </p>
                        {endpoint.type === 'email' && endpoint.verification_status === 'pending' && (
                          <span className="px-2 py-1 text-xs font-medium text-yellow-700 bg-yellow-100 rounded-full flex items-center flex-shrink-0">
                            Unverified
                          </span>
                        )}
                      </div>
                      {endpoint.type === 'email' && endpoint.verification_status === 'pending' && (
                        <div className="mt-2">
                          <button
                            onClick={() => handleResendVerification(endpoint.id)}
                            className="text-yellow-700 hover:text-yellow-900 text-sm underline hover:no-underline transition-all"
                            title="Resend verification email"
                          >
                            Resend Email
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 flex-shrink-0">
                    <label className="flex items-center whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={endpoint.is_active}
                        onChange={() => {}}
                        disabled={true}
                        className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 opacity-50 cursor-not-allowed`}
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                    
                    {!isPrimaryEmail(endpoint) && (
                      <button
                        onClick={() => handleDeleteEndpoint(endpoint.id)}
                        className="text-red-600 hover:text-red-700 p-1 flex-shrink-0"
                        title="Delete endpoint"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Help Text */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Note : </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Whatsapp support coming soon</li>
            </ul>
          </div>
        </div>

        {/* Account Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Name</span>
              <span className="font-medium text-gray-900">{authState.user?.name || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Email</span>
              <span className="font-medium text-gray-900">{authState.user?.email || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Subscription Tier</span>
              <span className="font-medium text-gray-900">
                {tierDisplayName[authState.user?.subscription_tier || ''] || 'Basic'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 